// Public OAuth callback route.
// The Lovable auth broker sets the Supabase session (via web_message in the
// preview iframe, or by returning tokens to `redirect_uri` in production).
// This route waits for that session to hydrate, then routes the user based on
// their role. It is intentionally public — no AuthGate — because it must be
// reachable before the session is established.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AlertCircle, Loader2, Shield } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { supabase } from "@/integrations/supabase/client";
import { authService } from "@/lib/services/authService";
import { LAUNCH_LOCKED, isBetaCustomerEmail } from "@/lib/launch-lock";

type Search = { redirect?: string; error?: string };

export const Route = createFileRoute("/auth/callback")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
    error: typeof s.error === "string" ? s.error : undefined,
  }),
  head: () => ({
    meta: [{ title: "ClaimGuard | Signing you in" }],
  }),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function finish() {
      try {
        // Give supabase-js a moment to set the session from the OAuth response.
        const { data } = await supabase.auth.getSession();
        let session = data.session;

        if (!session) {
          // Wait for the next auth state change (up to a few seconds).
          session = await new Promise((resolve) => {
            const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
              if (s) {
                sub.subscription.unsubscribe();
                resolve(s);
              }
            });
            setTimeout(() => {
              sub.subscription.unsubscribe();
              resolve(null);
            }, 6000);
          });
        }

        if (cancelled) return;
        if (!session?.user) {
          setError("We couldn't complete sign-in. Please try again.");
          return;
        }

        // Look up roles to decide where to send them.
        const roles = await authService.getRoles(session.user.id);
        const isStaff = roles.includes("staff") || roles.includes("admin");

        // Launch lock: block customer Google sign-ins that aren't allowlisted.
        // Staff/admin always get through.
        if (LAUNCH_LOCKED && !isStaff && !isBetaCustomerEmail(session.user.email ?? null)) {
          await supabase.auth.signOut();
          navigate({
            to: "/login",
            search: {
              redirect: undefined,
              staff: false,
            },
          });
          return;
        }

        const landing = authService.landingPathForRoles(roles);
        const target = redirect && redirect.startsWith("/") ? redirect : landing;
        navigate({ to: target });
      } catch (err) {
        console.error("[auth] callback failed:", err);
        setError("Sign-in failed. Please try again.");
      }
    }

    finish();
    return () => {
      cancelled = true;
    };
  }, [navigate, redirect]);

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <div className="container-page grid place-items-center py-24">
          <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-navy/10 text-navy">
              {error ? <AlertCircle className="h-6 w-6" /> : <Shield className="h-6 w-6" />}
            </div>
            {error ? (
              <>
                <h1 className="text-xl font-semibold tracking-tight">Sign-in problem</h1>
                <p className="mt-2 text-sm text-muted-foreground">{error}</p>
                <button
                  onClick={() => navigate({ to: "/login" })}
                  className="mt-6 inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:opacity-90"
                >
                  Back to sign in
                </button>
              </>
            ) : (
              <>
                <h1 className="text-xl font-semibold tracking-tight">Finishing sign-in…</h1>
                <p className="mt-2 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Just a moment
                </p>
              </>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
